import { useId } from 'react';
import { POST_TYPE_LABELS, TOPIC_LABELS } from '../constants';
import { safeHref } from '../links';
import type { ParsedItem } from '../types';
import { BookmarkStar } from './BookmarkStar';
import { MuteToggle } from './MuteToggle';

const ACCENT = '#6264A7';

// One independent-blogger post in the Yhteisö tab: topic and post-type
// badges, date, the same mute / bookmark controls as UpdateCard, the title as
// a link to the post (new tab), the author and the Finnish summary. Not
// expandable: a blog post has no analysis, impact or timeline data.
export function CommunityCard({
  item,
  bookmarked,
  onToggleBookmark,
  muted,
  onToggleMute,
}: {
  item: ParsedItem;
  bookmarked?: boolean;
  onToggleBookmark?: (item: ParsedItem) => void;
  // Only visible while muted when "Näytä kaikki" is on; rendered dimmed.
  muted?: boolean;
  onToggleMute?: (item: ParsedItem) => void;
}) {
  const titleId = useId();
  const topic = item.topic ?? 'other';
  const postType = item.postType ?? 'news';
  const author = item.author || item.sourceFeed;
  const published = item.publishedDate ? new Date(item.publishedDate) : null;
  const hasDate = published !== null && !isNaN(published.getTime());

  return (
    <article
      aria-labelledby={titleId}
      className="card-hover fade-in"
      style={{
        display: 'flex',
        background: 'var(--card-bg)',
        borderRadius: 10,
        overflow: 'hidden',
        marginBottom: 10,
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        border: '1px solid var(--border)',
        transition: 'filter 0.2s',
        // Same dimming as UpdateCard: `filter`, not `opacity` (.fade-in pins
        // opacity at 1).
        ...(muted ? { filter: 'opacity(0.5)' } : {}),
      }}
    >
      <div style={{ width: 4, flexShrink: 0, background: ACCENT }} />
      <div style={{ padding: '14px 16px', flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 8,
            flexWrap: 'wrap',
          }}
        >
          <Badge label={TOPIC_LABELS[topic]} color="#3B3A7A" background="#E8E8F7" />
          <Badge label={POST_TYPE_LABELS[postType]} color="#605E5C" background="#F3F2F1" />
          <span
            style={{
              fontSize: 11,
              color: 'var(--text-muted)',
              marginLeft: 'auto',
              flexShrink: 0,
            }}
          >
            {hasDate && (
              <time dateTime={item.publishedDate}>{published.toLocaleDateString('fi-FI')}</time>
            )}
          </span>
          {onToggleMute && <MuteToggle active={!!muted} onToggle={() => onToggleMute(item)} />}
          {onToggleBookmark && (
            <BookmarkStar active={!!bookmarked} onToggle={() => onToggleBookmark(item)} />
          )}
        </div>
        <h3
          id={titleId}
          style={{
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1.4,
            margin: '0 0 4px',
          }}
        >
          <a
            href={safeHref(item.sourceURL)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--text-primary)', textDecoration: 'none' }}
          >
            {item.title}
          </a>
        </h3>
        {author && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>{author}</div>
        )}
        <p
          style={{
            fontSize: 12,
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          {item.summaryFI}
        </p>
      </div>
    </article>
  );
}

// Text badge, so the topic / post type never relies on colour alone.
function Badge({
  label,
  color,
  background,
}: {
  label: string;
  color: string;
  background: string;
}) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color,
        background,
        padding: '2px 7px',
        borderRadius: 3,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}
